/**
 * MechaGuard - MapRenderer (地图渲染器)
 *
 * 负责绘制静态地形（科技地板 / 网格）、敌人行进路线（发光通道 + 流动虚线）、
 * 出怪口与基地核心。
 *
 * 所有绘制均使用「世界坐标」，调用前需要 Renderer.applyCamera()。
 */

import { TileType, TILE_SIZE } from './MapData.js';

const COLORS = {
  floorA: '#0a1729',
  floorB: '#0d1e34',
  laneBed: '#081525',
  grid: 'rgba(0, 140, 255, 0.10)',
  gridMajor: 'rgba(0, 200, 255, 0.18)',
  laneFill: 'rgba(8, 24, 44, 0.96)',
  laneGlow: 'rgba(0, 255, 255, 0.30)',
  laneEdge: 'rgba(0, 220, 255, 0.55)',
  dash: 'rgba(0, 255, 255, 0.75)',
  spawn: '#ff3366',
  base: '#00ff88',
};

export class MapRenderer {
  /** @param {ReturnType<import('./MapData.js').createMap>} map */
  constructor(map) {
    this.map = map;
    this.time = 0;
  }

  update(dt) {
    this.time += dt;
  }

  /** 绘制完整地图 */
  draw(ctx) {
    this.drawFloor(ctx);
    this.drawLane(ctx);
    this.drawSpawn(ctx);
    this.drawBase(ctx);
  }

  /* ===================================================
   * 地板
   * =================================================== */

  drawFloor(ctx) {
    const { cols, rows, tiles } = this.map;

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const t = tiles[r][c];
        const isLane = t === TileType.PATH || t === TileType.SPAWN || t === TileType.BASE;

        // 通道格也铺一层更暗的地板，避免通道两侧露出黑色空洞
        ctx.fillStyle = isLane
          ? COLORS.laneBed
          : ((r + c) % 2 === 0 ? COLORS.floorA : COLORS.floorB);

        ctx.fillRect(c * TILE_SIZE, r * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      }
    }

    // 细网格
    ctx.save();
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let c = 1; c < cols; c++) {
      const x = c * TILE_SIZE;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, rows * TILE_SIZE);
    }
    for (let r = 1; r < rows; r++) {
      const y = r * TILE_SIZE;
      ctx.moveTo(0, y);
      ctx.lineTo(cols * TILE_SIZE, y);
    }
    ctx.stroke();

    // 每 4 格一条主网格
    ctx.strokeStyle = COLORS.gridMajor;
    ctx.beginPath();
    for (let c = 4; c < cols; c += 4) {
      const x = c * TILE_SIZE;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, rows * TILE_SIZE);
    }
    for (let r = 4; r < rows; r += 4) {
      const y = r * TILE_SIZE;
      ctx.moveTo(0, y);
      ctx.lineTo(cols * TILE_SIZE, y);
    }
    ctx.stroke();
    ctx.restore();
  }

  /* ===================================================
   * 敌人通道
   * =================================================== */

  drawLane(ctx) {
    const pts = this.map.waypoints;
    if (pts.length < 2) return;

    const laneWidth = TILE_SIZE * 0.62;

    const tracePath = () => {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i].x, pts[i].y);
      }
    };

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // 1) 通道底色
    tracePath();
    ctx.strokeStyle = COLORS.laneFill;
    ctx.lineWidth = laneWidth;
    ctx.stroke();

    // 2) 外发光
    tracePath();
    ctx.strokeStyle = COLORS.laneGlow;
    ctx.lineWidth = laneWidth;
    ctx.shadowColor = COLORS.laneEdge;
    ctx.shadowBlur = 18;
    ctx.stroke();
    ctx.shadowBlur = 0;

    // 3) 明亮边缘（细线描边效果）
    tracePath();
    ctx.strokeStyle = COLORS.laneEdge;
    ctx.lineWidth = 2;
    ctx.stroke();

    // 4) 流动虚线：指向基地方向
    tracePath();
    ctx.setLineDash([14, 20]);
    ctx.lineDashOffset = -this.time * 55;
    ctx.strokeStyle = COLORS.dash;
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.restore();
  }

  /* ===================================================
   * 出怪口 / 基地
   * =================================================== */

  drawSpawn(ctx) {
    const { x, y } = this.map.spawn;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 3);
    const r = TILE_SIZE * (0.34 + pulse * 0.08);

    ctx.save();

    ctx.shadowColor = COLORS.spawn;
    ctx.shadowBlur = 22;

    // 外环
    ctx.strokeStyle = COLORS.spawn;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();

    // 内环（反向旋转）
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(-this.time * 1.4);
    ctx.setLineDash([6, 10]);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.66, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    // 核心
    ctx.fillStyle = `rgba(255, 51, 102, ${0.25 + pulse * 0.3})`;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.42, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  drawBase(ctx) {
    const { x, y } = this.map.base;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 2.2);
    const r = TILE_SIZE * 0.4;

    ctx.save();
    ctx.translate(x, y);

    ctx.shadowColor = COLORS.base;
    ctx.shadowBlur = 24;

    // 六边形核心
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      const px = Math.cos(a) * r;
      const py = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();

    ctx.fillStyle = `rgba(0, 255, 136, ${0.12 + pulse * 0.18})`;
    ctx.fill();
    ctx.strokeStyle = COLORS.base;
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // 旋转外环
    ctx.rotate(this.time * 0.8);
    ctx.setLineDash([8, 12]);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(0, 255, 136, 0.7)';
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.restore();
  }
}
